import { Map } from 'immutable'
import { filterUndefined } from '@shared/Frontend/utils/filterUndefined'
import { StrictOmit } from '@shared/Frontend/utils/strictOmit'
import { Metadata } from '@shared/Hoppinger.Metadata/Frontend/metadata'
import { AsyncState, Maybe } from 'ballerina-core'
import { SVGIconName } from '../../../../../svgIcons'
import { actionsParser, ActionsResponse } from '../shared/actions/actions.parser'
import { brochureSrcSet, cmsImageParser, ctaSrcSet, defaultSrcSet, FileResponse, gridSrcSet, homeHeaderSrcSet, ImageResponse, logoSrcSet, mediaSrcSet, pageHeaderSrcSet, quoteSrcSet, simpleGridSrcSet, textImageSrcSet } from '../shared/cmsImage/cmsImage.parser'
import { cmsLinkParser, LinkKind, LinkResponse as linkResponse } from '../shared/cmsLink/cmsLink.parser'
import {
  BackgroundColor,
  Block,
  ContactItem,
  DownloadBlockItem,
  ExpandableBlockItem,
  FactsAndFiguresBlockItem,
  GridBlockItem,
  ImagePosition,
  LabelTitleIntroduction,
  LinkListItem,
  LogowallBlockItem,
  MediaBlockItem,
  MediaItem,
  RichDownloadsBlockItem,
  Socials,
} from './blocks/blocks.state'
import { MediaGalleryItemState } from './blocks/mediaGalleryBlock/mediaGalleryBlock.state'
import { Category, CMSPage, LocalizationLinks, SocialLink, SocialLinkPlatform } from './cmsPage.state'
import { initialFiltersState } from './filters/filters.state'
import { initialTeasersAsync, TeaserKind } from './teasers/teasers.state'

export type PageResponse = {
  slug: string
  title: string
  buildingBlocks?: string
  headerInfo: PageInfoResponse & ActionsResponse
  publicationDate?: string
  metadata: Metadata
  locale: string
  breadcrumbs: Array<{ url: string; title: string }>
  localizationLinks?: { [locale: string]: { term: string; id: number } }
} & (
  | {
      kind:
        | 'Generic'
        | 'Home'
        | 'About'
        | 'Cookies'
        | 'Terms'
        | 'Privacy'
        | 'Contact'
        | 'SearchOverview'
        | 'NewsOverview'
        | 'OurViewOverview'
        | 'PersonOverview'
    }
  | { kind: 'News'; categories: Array<Category> }
  | { kind: 'StandpointsOverview'; gridOrList?: boolean }
  | { kind: 'Standpoints'; }
  | { kind: 'OurView'; }
  | { kind: 'Person'; person: PersonResponse }
)
export type PageInfoResponse = { title: string; introduction?: string; image?: ImageResponse }

export type BlockResponse =
  | ({ kind: 'Text' | 'TextMini'; text: string } & LabelTitleIntroductionResponse & ActionsResponse)
  | ({
      kind: 'TextWithImage'
      text: string
      imagePosition: ImagePosition
      image: ImageResponse
    } & LabelTitleIntroductionResponse &
      ActionsResponse)
  | ({
      kind: 'Quote'
      text: string
      author?: string
      authorDescription?: string
      image?: ImageResponse
    } & LabelTitleIntroductionResponse &
      ActionsResponse)
  | ({ kind: 'Media'; items: Array<MediaBlockItemResponse> } & LabelTitleIntroductionResponse & ActionsResponse)
  | ({ kind: 'MediaGallery'; items: Array<MediaItemResponse> } & LabelTitleIntroductionResponse & ActionsResponse)
  | ({
      kind: 'LinksList'
      items: { link?: linkResponse; title?: string; description: string }[]
    } & LabelTitleIntroductionResponse &
      ActionsResponse)
  | ({
      kind: 'LogoWall'
      items: Array<{ name: string; link?: linkResponse; logo: ImageResponse }>
    } & LabelTitleIntroductionResponse &
      ActionsResponse)
  | ({
      kind: 'CTA'
      backgroundColor?: BackgroundColor
      image?: ImageResponse
      displaySize: DisplaySize
    } & LabelTitleIntroductionResponse &
      ActionsResponse)
  | ({
      kind: 'DownloadList'
      documents: Array<{ title: string; description: string; document: FileResponse }>
    } & LabelTitleIntroductionResponse &
      ActionsResponse)
  | ({
      kind: 'RichDownloads'
      paginationSize: number
      items: Array<{
        title: string
        label?: string
        image: ImageResponse
        units: Array<{ title: string; document: FileResponse; isDownload: boolean }>
      }>
    })
  | ({ kind: 'ContactBlock'; person: PersonResponse; location: LocationResponse } & LabelTitleIntroductionResponse &
      ActionsResponse)
  | ({
      kind: 'ContactGrid'
      people: Array<PersonResponse>
      location: LocationResponse
    } & LabelTitleIntroductionResponse &
      ActionsResponse)
  | ({
      kind: 'Expandable'
      items: Array<{ title: string; text: string; subtitle: string }>
    } & LabelTitleIntroductionResponse &
      ActionsResponse)
  | ({
      kind: 'FactsAndFigures'
      items: Array<{ description: string; value: string }>
    } & LabelTitleIntroductionResponse &
      ActionsResponse)
  | ({
      kind: 'FeaturedContent'
      imagePosition: ImagePosition
      featuredContent: { locale: string; pagetitle: string; slug: string; teaserInfo: PageInfoResponse }
    } & LabelTitleIntroductionResponse &
      ActionsResponse)
  | ({
      kind: 'Grid' | 'GridSimple'
      variant: GridVariant
      items: Array<{
        label: string
        title: string
        introduction?: string
        link: { kind: LinkKind; url: string; isPublished: boolean, label: string }
        image?: ImageResponse
        icon?: SVGIconName
      }>
    } & LabelTitleIntroductionResponse &
      ActionsResponse)
  | ({
      kind: 'Iframe';
      iframeTitle: string
      label: string
      link: string
    } & LabelTitleIntroductionResponse &
      ActionsResponse)
  | ({
    kind: 'RecentContent'
    linkedContent: string
  } & LabelTitleIntroductionResponse & ActionsResponse)
  | ({
      kind: 'SidebarNavigation'
      items: { link?: linkResponse; title?: string; description: string }[]
    } & LabelTitleIntroductionResponse)
  | ({ kind: 'BmiCalculator'; } & LabelTitleIntroductionResponse)

export type LabelTitleIntroductionResponse = { introduction?: string; label?: string; title?: string }

type DisplaySize = 'FullWidth' | 'ContentWidth'

type GridVariant = 'Image' | 'Icon' | 'Text'

type MediaBlockItemResponse = MediaItemResponse & { displaySize?: DisplaySize }

type MediaItemResponse = {
  caption?: string
  source?: string
  kind: 'Image' | 'Video'
  media?: ImageResponse
  url?: string
}

type LocationResponse = { contactDetails?: string; name: string }

type PersonResponse = {
  name: string
  email?: string
  function?: string
  phoneNumber?: string
  image: ImageResponse
  socialLinks: Array<SocialLink>
}

export const cmsPageParser = (json: PageResponse): CMSPage => {
  const blocks: Array<Block> = json.buildingBlocks
    ? JSON.parse(json.buildingBlocks)
      .map((b: BlockResponse): Block => blockParser(b))
      .filter((b: BlockResponse) => !!b) //Filter out blocks that couldn't be parsed
    : []
  const pageBase = pageBaseParser(json)
  switch (json.kind) {
    case 'Generic':
      return { kind: 'generic', blocks, ...pageBase }
    case 'Home':
      return { kind: 'home', blocks, ...pageBase }
    case 'Cookies':
      return { kind: 'cookies', blocks, ...pageBase }
    case 'Terms':
      return { kind: 'terms', blocks, ...pageBase }
    case 'Privacy':
      return { kind: 'privacy', blocks, ...pageBase }
    case 'About':
      return { kind: 'about', blocks, ...pageBase }
    case 'Contact':
      return { kind: 'contact', blocks, ...pageBase }
    case 'Person':
      return {
        kind: 'person',
        blocks,
        name: json.person.name,
        email: json.person.email,
        function: json.person.function,
        phoneNumber: json.person.phoneNumber,
        image: cmsImageParser(defaultSrcSet)(json.person.image),
        socialLinks: json.person.socialLinks,
        ...pageBase,
      }
    case 'News':
      return {
        kind: 'news',
        blocks,
        categories: json.categories,
        ...pageBase,
      }
    case 'Standpoints':
      return { kind: 'standpoints', blocks, ...pageBase }
    case 'OurView':
      return { kind: 'ourview', blocks, ...pageBase }
    case 'NewsOverview':
      return { kind: 'news overview', filters: initialFiltersState(), teasers: { items: initialTeasersAsync() }, ...pageBase }
    case 'OurViewOverview':
      return { kind: 'ourview overview', filters: initialFiltersState(), teasers: { items: initialTeasersAsync() }, ...pageBase }
    case 'StandpointsOverview':
      return { kind: 'standpoints overview', filters: initialFiltersState(), teasers: { items: initialTeasersAsync() }, ...pageBase}
    case 'PersonOverview':
      return { kind: 'person overview', filters: initialFiltersState(), teasers: { items: initialTeasersAsync() }, ...pageBase }
    case 'SearchOverview':
      const url = new URL(window.location.href)
      const params = Map<string, string>(url.searchParams)
      const searchQuery = params.get("searchQuery")
      return {
        kind: 'search',
        filters: initialFiltersState(),
        teasers: { items: initialTeasersAsync() },
        searchQuery: searchQuery || '',
        ...pageBase,
      }
  }
}

const pageBaseParser = ({
  kind,
  metadata,
  headerInfo,
  breadcrumbs,
  localizationLinks,
  slug,
  locale,
  publicationDate
}: PageResponse): StrictOmit<CMSPage, 'kind'> => ({
  metaTitle: metadata.title,
  header: {
    title: headerInfo.title,
    introduction: headerInfo.introduction,
    image: Maybe.Operations.map(cmsImageParser(kind == 'Home' ? homeHeaderSrcSet : pageHeaderSrcSet))(headerInfo.image),
    publicationDate: publicationDate ? new Date(publicationDate) : undefined,
    ...actionsParser(headerInfo),
  },
  breadcrumbs,
  localizationLinks: localizationLinksParser(localizationLinks),
  slug,
  locale,
})

const labelTitleIntroParser = ({
  label,
  title,
  introduction,
}: LabelTitleIntroductionResponse): LabelTitleIntroduction => ({
  label,
  title,
  introduction,
})

const mediaItemParser = ({ caption, source, kind, media, url }: MediaItemResponse): Maybe<MediaItem> =>
  kind === 'Image'
    ? Maybe.Operations.map<ImageResponse, MediaItem>((image) => ({
        kind: 'image',
        image: cmsImageParser(mediaSrcSet)(image),
        caption,
        source,
      }))(media)
    : Maybe.Operations.map<ImageResponse, MediaItem>((media) => ({ kind: 'video', url: media.url, caption, source }))(media)

const gridVariantParser = (variant: GridVariant): Lowercase<GridVariant> => {
  switch (variant) {
    case 'Image':
      return 'image'
    case 'Icon':
      return 'icon'
    case 'Text':
      return 'text'

    default:
      return 'text'
  }
}

const personParser = ({
  image,
  name,
  function: functionn,
  email,
  phoneNumber,
  socialLinks,
}: PersonResponse): ContactItem => ({
  image: image ? cmsImageParser(defaultSrcSet)(image) : undefined,
  personName: name,
  personFunction: functionn,
  personEmail: email,
  personPhone: phoneNumber,
  ...socialLinksParser(socialLinks),
})

const socialLinksParser = (socialLinks: Array<SocialLink>): Socials => {
  const socialLinksMap: { [key in SocialLinkPlatform]: string | undefined } = {
    Website: undefined,
    Facebook: undefined,
    Twitter: undefined,
    LinkedIn: undefined,
    Instagram: undefined,
  }

  socialLinks.forEach((sl) => {
    socialLinksMap[sl.type] = sl.url
  })

  return {
    personWebsite: socialLinksMap['Website'],
    personFacebook: socialLinksMap['Facebook'],
    personLinkedIn: socialLinksMap['LinkedIn'],
    personInstagram: socialLinksMap['Instagram'],
    personTwitter: socialLinksMap['Twitter'],
  }
}

const localizationLinksParser = (localizationLinks: PageResponse['localizationLinks']): LocalizationLinks => {
  const links: LocalizationLinks = {}

  if (!localizationLinks) return links

  Object.entries(localizationLinks).map(([locale, { term: slug, id }]) => {
    links[locale] = { slug, id }
  })

  return links
}

// The styling needs any <table> element wrapped within a <div class="table"> element
//   This function does a simple regex replace to do this
export const wrapTablesInHtml = (html: string): string =>
  (html || '').replace(/<table\b[^>]*>[\s\S]*?<\/table>/gi, '<div class="table">$&</div>')

const blockParser = (block: BlockResponse): Block => {
  switch (block.kind) {
    case 'Text':
      return {
        kind: 'textBlock',
        text: wrapTablesInHtml(block.text),
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'TextMini':
      return {
        kind: 'textMiniBlock',
        text: wrapTablesInHtml(block.text),
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'TextWithImage':
      return {
        kind: 'textWithImageBlock',
        text: wrapTablesInHtml(block.text),
        imagePosition: block.imagePosition,
        image: cmsImageParser(textImageSrcSet)(block.image),
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'Expandable':
      return {
        kind: 'expandableBlock',
        items: block.items.map(
          ({ title, subtitle, text }): ExpandableBlockItem => ({
            title,
            description: subtitle,
            body: wrapTablesInHtml(text),
            nestedBlocks: [],
          })
        ),
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'FactsAndFigures':
      return {
        kind: 'factsAndFiguresBlock',
        items: block.items.map(({ value, description }): FactsAndFiguresBlockItem => ({ value, description })),
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'FeaturedContent':
      return {
        kind: 'featuredContentBlock',
        imagePosition: block.imagePosition,
        image: Maybe.Operations.map(cmsImageParser(defaultSrcSet))(block.featuredContent.teaserInfo.image),
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'Quote':
      return {
        kind: 'quoteBlock',
        text: block.text,
        author: block.author,
        authorDescription: block.authorDescription,
        image: Maybe.Operations.map(cmsImageParser(quoteSrcSet))(block.image),
        ...actionsParser(block),
      }
    case 'Media':
      return {
        kind: 'mediaBlock',
        items: filterUndefined(
          block.items.map(
            ({ displaySize, ...rest }): Maybe<MediaBlockItem> =>
              Maybe.Operations.map<MediaItem, MediaBlockItem>(
                (mediaItem): MediaBlockItem => ({
                  ...mediaItem,
                  displayOption: displaySize === 'FullWidth' ? 'full-width' : 'content-width',
                })
              )(mediaItemParser(rest))
          )
        ),
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'MediaGallery':
      return {
        kind: 'mediaGalleryBlock',
        items: filterUndefined(
          block.items.map(
            (item): Maybe<MediaGalleryItemState> =>
              Maybe.Operations.map<MediaItem, MediaGalleryItemState>((item) => ({ ...item, isForeground: false }))(
                mediaItemParser(item)
              )
          )
        ),
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'LinksList':
      return {
        kind: 'linkListBlock',
        items:
          block.items
            ?.filter((i) => i.link?.url)
            .map(({ link, title, description }): LinkListItem => ({ subTitle: title, link: cmsLinkParser(link) })) ||
          [],
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'SidebarNavigation':
      return {
        kind: 'sidebarNavigation',
        items:
          block.items
            ?.filter((i) => i.link?.url)
            .map(({ link, title, description }): LinkListItem => ({ subTitle: title, link: cmsLinkParser(link) })) ||
          [],
        ...labelTitleIntroParser(block)
      }
    case 'LogoWall':
      return {
        kind: 'logoWallBlock',
        items:
          block.items?.map(({ name, link, logo }): LogowallBlockItem => ({ name, link: cmsLinkParser(link), logo: cmsImageParser(logoSrcSet)(logo) })) ||
          [],
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'CTA':
      return {
        kind: 'ctaBlock',
        image: block.image && cmsImageParser(ctaSrcSet)(block.image),
        displayOption: block.displaySize === 'FullWidth' ? 'full-width' : 'content-width',
        bgColour: block.backgroundColor,
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'DownloadList':
      return {
        kind: 'downloadBlock',
        items: block.documents.map(
          ({ title, description, document }): DownloadBlockItem => ({ title, description, url: document.url })
        ),
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'RichDownloads':
      return {
        kind: 'richDownloadsBlock',
        paginationSize: block.paginationSize,
        items: block.items.map(
          ({ title, label, image, units }): RichDownloadsBlockItem => ({
            title,
            label,
            image: cmsImageParser(brochureSrcSet)(image),
            units: units.map(({ title, document, isDownload }) => ({ title, url: document.url, isDownload })),
          })
        ),
      }
    case 'ContactBlock':
      return {
        kind: 'contactBlock',
        ...personParser(block.person),
        ...socialLinksParser(block.person.socialLinks),
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'ContactGrid':
      return {
        kind: 'contactGridBlock',
        items: block.people.map(personParser),
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'Grid':
    case 'GridSimple':
      const srcSet = (kind: typeof block.kind) => {
        switch (kind) {
          case 'Grid':
            return gridSrcSet
          case 'GridSimple':
            return simpleGridSrcSet
        }
      }
      return {
        kind: block.kind === 'Grid' ? 'gridBlock' : 'gridSimpleBlock',
        variant: gridVariantParser(block.variant),
        items: block.items.map(
          ({ link, image, icon, ...rest }): GridBlockItem => ({
            ...labelTitleIntroParser(rest),
            link: link && link.isPublished ? { external: link.kind === 'External', url: link.url, label: link.label } : undefined,
            image: image && cmsImageParser(srcSet(block.kind))(image),
            icon,
          })
        ),
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'Iframe':
      return {
        kind: 'iframe',
        iframeTitle: block.iframeTitle,
        link: block.link,
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }

    case 'RecentContent':
      return {
        kind: 'recentContentBlock',
        linkedContent: block.linkedContent as TeaserKind,
        items: AsyncState.Default.unloaded(),
        ...labelTitleIntroParser(block),
        ...actionsParser(block),
      }
    case 'BmiCalculator':
      return {
        kind: 'bmiCalculator',
        ...labelTitleIntroParser(block)
      }
  }
}
